import { useCallback, useEffect, useRef, type RefObject } from 'react';

/** Every element inside a dialog panel that a keyboard can land on. */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * The dialogs open right now, oldest first. Only the last one answers Tab and Escape: a tour
 * popover (`Spotlight`) over a `Sheet` would otherwise have both trapping focus in their own panel
 * at once, and one Escape would close both (MYP-83).
 */
const openDialogs: object[] = [];

/**
 * The page's own `overflow`, saved by the first dialog to open and put back by the last to close.
 *
 * Saving it per dialog went wrong when two closed out of order — a tour over the Add package form,
 * then Back: the form restored the page's value, the tour closed later and restored the "hidden" it
 * had found, and the page could no longer scroll. Counting the open dialogs makes the order
 * irrelevant.
 */
let pageOverflow = '';

export interface DialogBehaviorOptions {
  /** Whether Escape closes the dialog. See `Modal`'s own doc comment on what "false" is for. */
  dismissible: boolean;
  onClose: () => void;
  /** Changing this re-places focus inside the panel — see `Modal`'s `focusKey` doc comment. */
  focusKey?: string | number;
  /**
   * The control to focus when the panel opens or `focusKey` changes, instead of the first one.
   * For a panel whose first control is not the expected next move — a tour's "Skip" ahead of its
   * "Next". Falls back to the first control when it is missing or disabled.
   */
  initialFocusRef?: RefObject<HTMLElement | null>;
}

/**
 * The overlay plumbing every full-screen dialog needs, independent of what it looks like: trap
 * Tab inside the panel, close on Escape, lock page scroll while open, return focus to whatever
 * opened it on unmount, and place focus inside the panel on mount or whenever `focusKey` changes.
 *
 * Extracted out of `Modal` (MYP-33) so `Sheet` — a differently-shaped overlay that needs the same
 * behavior — doesn't reimplement the handful of parts that are easy to get subtly wrong a second
 * time. `Modal`'s doc comment explains why they're worth writing once; this is that once.
 */
export function useDialogBehavior<T extends HTMLElement = HTMLElement>({
  dismissible,
  onClose,
  focusKey,
  initialFocusRef,
}: DialogBehaviorOptions) {
  // The type param is the caller's own panel element (a Modal's <div>, a Sheet's <aside>) — kept
  // generic here since this hook doesn't render anything itself and has no reason to pick one.
  const panelRef = useRef<T>(null);
  // This dialog's place in `openDialogs` — any unique object will do.
  const token = useRef({});

  const focusable = useCallback(
    () => Array.from(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []),
    [],
  );

  const focusPanel = useCallback(() => {
    const preferred = initialFocusRef?.current;
    if (preferred && !preferred.hasAttribute('disabled') && panelRef.current?.contains(preferred)) {
      preferred.focus();
      return;
    }
    const first = focusable()[0];
    if (first) first.focus();
    else panelRef.current?.focus();
  }, [focusable, initialFocusRef]);

  // Mount and unmount only — see Modal.tsx for why this must not depend on `dismissible`.
  useEffect(() => {
    const self = token.current;
    openDialogs.push(self);
    const opener = document.activeElement as HTMLElement | null;
    if (openDialogs.length === 1) pageOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      openDialogs.splice(openDialogs.indexOf(self), 1);
      if (openDialogs.length === 0) document.body.style.overflow = pageOverflow;
      opener?.focus();
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // A dialog opened over this one has the keyboard until it closes.
      if (openDialogs.at(-1) !== token.current) return;
      // Already handled by a control inside the panel — an open `Select` closing on Escape, or
      // keeping Tab in its list (MYP-137). Without this, one Escape would close the dropdown and
      // the whole dialog with it.
      if (event.defaultPrevented) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        if (dismissible) onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const panel = panelRef.current;
      if (!panel) return;

      const items = focusable();
      const first = items[0];
      const last = items[items.length - 1];

      if (!first || !last) {
        event.preventDefault();
        panel.focus();
        return;
      }

      if (!panel.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dismissible, focusable, onClose]);

  useEffect(() => {
    focusPanel();
  }, [focusKey, focusPanel]);

  return { panelRef };
}
